import { useRef, useCallback, useState } from "react";
import {
  UseFormProps,
  FieldValues,
  UseFormReturn,
  useForm,
  useFormContext,
} from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { IntlShape } from "react-intl";
import * as v from "valibot";
import { BANNED_USERNAMES } from "./banned-values";
import { BaseIssue, BaseSchema, InferOutput, InferInput } from "valibot";
import { useSearchParams } from "react-router";
import { useOnChangeEffect } from "./hooks";
import qs from "qs";

export const MIN_PASSWORD_LENGTH = 6;
export const MAX_VARCHAR_LENGTH = 255;

export type UseValibotFormReturn<
  TItem extends
    | v.BaseSchema<FieldValues, FieldValues, v.BaseIssue<unknown>>
    | v.BaseSchemaAsync<FieldValues, FieldValues, v.BaseIssue<unknown>>,
  TContext = unknown,
> = UseFormReturn<v.InferInput<TItem>, TContext, v.InferOutput<TItem>>;

export const useValibotForm = <
  TItem extends
    | v.BaseSchema<FieldValues, FieldValues, v.BaseIssue<unknown>>
    | v.BaseSchemaAsync<FieldValues, FieldValues, v.BaseIssue<unknown>>,
  TContext = unknown,
>(
  schema: TItem,
  props: Omit<UseFormProps<v.InferInput<TItem>, TContext>, "resolver"> & {
    resolverOptions?: { mode?: "sync" | "async" };
  } = {},
): UseFormReturn<v.InferInput<TItem>, TContext, v.InferOutput<TItem>> =>
  useForm<v.InferInput<TItem>, TContext, v.InferOutput<TItem>>({
    ...props,
    resolver: valibotResolver(schema, undefined, {
      mode: props.resolverOptions?.mode,
    }),
  });

const PARSE_OPTIONS: qs.IParseOptions = {
  allowDots: true,
  depth: 10,
};

const STRINGIFY_OPTIONS: qs.IStringifyOptions = {
  allowDots: true,
  encodeValuesOnly: true,
  arrayFormat: "brackets",
  skipNulls: true,
};

const DEFAULT_OPTIONS = {
  preventScrollReset: true,
  replace: true,
} as const;

export const useValibotFormContext = <
  TItem extends
    | v.BaseSchema<FieldValues, FieldValues, v.BaseIssue<unknown>>
    | v.BaseSchemaAsync<FieldValues, FieldValues, v.BaseIssue<unknown>>,
  TContext = unknown,
>(): UseFormReturn<v.InferInput<TItem>, TContext, v.InferOutput<TItem>> =>
  useFormContext<v.InferInput<TItem>, TContext, v.InferOutput<TItem>>();

type Primitives = string | number | object | undefined | null | Primitives[];

export type InputSearchParams = Record<string, Primitives>;
export type OutputSearchParams = Record<string, unknown | undefined | null>;

export type TIemSchema =
  | v.BaseSchema<InputSearchParams, OutputSearchParams, v.BaseIssue<unknown>>
  | v.BaseSchemaAsync<
      InputSearchParams,
      OutputSearchParams,
      v.BaseIssue<unknown>
    >;

export const useValibotSearchParams = <TItem extends TIemSchema>(
  schema: TItem,
  options?: {
    navigate?: boolean;
    preventScrollReset?: boolean;
    replace?: boolean;
  },
) => {
  const [parsedParams, setParsedParams] = useState<
    (v.SafeParseResult<TItem> & { loading: false }) | { loading: true }
  >({
    loading: true,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  useOnChangeEffect(searchParams, () => {
    setParsedParams({ loading: true });
    const params = qs.parse(searchParams.toString(), PARSE_OPTIONS);
    v.safeParseAsync(schema, params).then((result) => {
      setParsedParams({ loading: false, ...result });
    });
  });
  const setParams = useCallback(
    (params: InputSearchParams) => {
      const current = qs.parse(searchParams.toString(), PARSE_OPTIONS);
      const merged = { ...current, ...params };

      for (const key of Object.keys(merged))
        if (merged[key] === null) delete merged[key];

      v.safeParseAsync(schema, merged).then((result) => {
        if (!result.success) return;
        setSearchParams(
          qs.stringify(
            { ...merged, ...result.output },

            STRINGIFY_OPTIONS,
          ),
          { ...DEFAULT_OPTIONS, ...options },
        );
      });
    },
    [schema, searchParams, setSearchParams, options],
  );
  return [parsedParams, setParams] as const;
};

export const getSearchParams = (request: Request) => {
  return qs.parse(new URL(request.url).searchParams.toString(), PARSE_OPTIONS);
};

export const parseRequestSearch = <
  TItem extends v.BaseSchema<
    InputSearchParams,
    OutputSearchParams,
    v.BaseIssue<unknown>
  >,
>(
  schema: TItem,
  request: Request,
): v.InferOutput<TItem> => {
  const parsed = v.safeParse(schema, getSearchParams(request));
  return parsed.success ? parsed.output : {};
};

export const isNotBannedUsername = (allowed: string[]) => (value: string) =>
  allowed.includes(value) || !BANNED_USERNAMES.includes(value);

export const isUrl = (value: string) => {
  if (!value) return true;
  try {
    new URL(value);
  } catch (_e) {
    return false;
  }
  return true;
};

export const maxFileSize = (size: number) => {
  return (files: FileList | undefined) => {
    if (files && files.length > 0) {
      return files[0].size < size;
    }
    return true;
  };
};

export const imgDimensionsWithin = <Input extends File | Blob>({
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
}: {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}) => {
  return (file: Input): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => {
        const img = new Image();
        img.onload = () => {
          if (
            (minWidth && img.width < minWidth) ||
            (minHeight && img.height < minHeight) ||
            (maxWidth && img.width > maxWidth) ||
            (maxHeight && img.height > maxHeight)
          ) {
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.onerror = (err) => {
          reject(err);
        };
        img.src = fr.result as string;
      };
      fr.onerror = (err) => {
        reject(err);
      };
      fr.readAsDataURL(file);
    });
  };
};

interface FileUploadOptions {
  name?: string;
  maxMb?: number;
  mimeTypes?: `${string}/${string}`[];
}

export const fileUpload = (
  intl: IntlShape,
  { name = "File", maxMb = 20, mimeTypes = [] }: FileUploadOptions = {},
) =>
  v.pipe(
    v.file(),
    v.maxSize(
      1024 * 1024 * maxMb,
      intl.formatMessage(
        {
          defaultMessage: "{name} must be smaller than {maxMb} MB.",
        },
        { name, maxMb },
      ),
    ),
    mimeTypes.length > 0
      ? v.mimeType(
          mimeTypes,
          intl.formatMessage(
            {
              defaultMessage: "{name} must be {mimeTypes}",
            },
            {
              name,
              mimeTypes: intl.formatList(
                mimeTypes.map(
                  (mimeType) =>
                    mimeType.split("/")[1].split(";")[0].split("+")[0],
                ),
                { type: "disjunction" },
              ),
            },
          ),
        )
      : v.check((_) => true),
  );

export const imageUpload = (
  intl: IntlShape,
  {
    name = "Image",
    mimeTypes = ["image/png", "image/jpeg"],
    ...options
  }: FileUploadOptions = {},
) => fileUpload(intl, { name, mimeTypes, ...options });

export const isOrdered = <T>(arr: T[]): boolean =>
  arr.every((value, index, array) => index === 0 || value >= array[index - 1]);

export interface AsyncCheckCallback<T> {
  (data: T, ctx: { signal: AbortSignal }): boolean | Promise<boolean>; // Callback function signature
}

export interface AsyncCheckOptions {
  debounce?: number;
}

export interface AsyncCheck<T> {
  (data: T): Promise<boolean>;
}

interface AsyncCheckCtx<T> {
  input: T | null;
  output: Promise<boolean> | boolean | null;
  timeout: ReturnType<typeof setTimeout> | null;
  abortContoller: AbortController | null;
}

const abortAsyncCheck = (ctx: AsyncCheckCtx<unknown>) => {
  if (ctx.timeout) {
    clearTimeout(ctx.timeout);
    ctx.timeout = null;
  }
  if (ctx.abortContoller) {
    ctx.abortContoller.abort();
    ctx.abortContoller = null;
  }
};

const runAsyncCheck = async <T>(
  ctx: AsyncCheckCtx<T>,
  callback: AsyncCheckCallback<T>,
  options: AsyncCheckOptions,
  data: T,
): Promise<boolean> => {
  await new Promise<void>((resolve) => {
    ctx.timeout = setTimeout(() => {
      resolve();
    }, options.debounce);
  });
  ctx.abortContoller = new AbortController();
  const signal = ctx.abortContoller.signal;
  const result = callback(data, {
    signal,
  });
  if (signal.aborted) {
    return new Promise(() => {});
  } else {
    return result;
  }
};

export function useAsyncCheck<T>(
  callback: AsyncCheckCallback<T>,
  options: AsyncCheckOptions = {},
): AsyncCheck<T> {
  const ctx = useRef<AsyncCheckCtx<T>>({
    input: null,
    output: null,
    timeout: null,
    abortContoller: null,
  });
  return useCallback(
    async (data: T): Promise<boolean> => {
      if (
        ctx.current.input === data &&
        ctx.current.output !== null &&
        ctx.current.timeout !== null &&
        ctx.current.abortContoller !== null &&
        !ctx.current.abortContoller.signal.aborted
      ) {
        return ctx.current.output;
      }
      abortAsyncCheck(ctx.current);
      ctx.current.input = data;
      ctx.current.output = runAsyncCheck(ctx.current, callback, options, data);
      return ctx.current.output;
    },
    [callback, options],
  );
}
export const createSchemaWithIntl = <
  TInput,
  TOutput,
  TIssue,
  TSchema extends BaseSchema<TInput, TOutput, BaseIssue<TIssue>>,
>(
  schemaFactory: (intl: IntlShape) => TSchema,
) => {
  return schemaFactory;
};

export type InferFormOutput<
  T extends (
    intl: IntlShape,
  ) => BaseSchema<unknown, unknown, BaseIssue<unknown>>,
> = InferOutput<ReturnType<T>>;

export type InferFormInput<
  T extends (
    intl: IntlShape,
  ) => BaseSchema<unknown, unknown, BaseIssue<unknown>>,
> = InferInput<ReturnType<T>>;
